import { createContext, useContext, useEffect, forwardRef, useRef, useImperativeHandle, useCallback, memo } from 'react';
import { Icon } from '@/components/Icon'
import TextareaAutosize from 'react-textarea-autosize';
import { TextboxList } from '@/form/TextboxList';
import { useFormField, useFormControl } from '@/form/FormProvider';
import { GenericDropdown } from '@/components/GenericDropdown';
import { validateEmail } from '@/shared/utils';
import { MDXEditor, headingsPlugin, quotePlugin, listsPlugin, toolbarPlugin, BlockTypeSelect, BoldItalicUnderlineToggles, ListsToggle, UndoRedo, markdownShortcutPlugin, thematicBreakPlugin } from '@mdxeditor/editor'
import '@mdxeditor/editor/style.css'
import { useFilePickerModal } from '@/components/FilePickerModals';

export { TextboxList };

export function ButtonLabel({ title }) {
  return <label className="for_button">{title}</label>;
}

export function ButtonHint({ hint }) {
  return <div className="input_hint for_button">{hint}</div>;
}

export function Button({ icon, small_icon = false, text, className, backgroundColor, onClick, submit, icon_right, loading, ...args }) {

  let { disabled, readOnly, obviated } = useFormControl({ ...args });

  return <>
    <div className={"button_outer"+(className ? " "+className : "")} style={{backgroundColor: backgroundColor ? backgroundColor : "transparent"}}>
      <button
        type={submit ? "submit" : "button"}
        className={"button" + ((icon && !text) ? " round" : "") + (small_icon ? " small_icon" : "") + (className ? " "+className : "") + ((disabled) ? " disabled" : "") + (loading ? " loading" : "")}
        onClick={(onClick && !(disabled || obviated)) ? onClick : (() => {})}
        inert={(disabled || obviated) ? "true" : undefined}
      >
        {(icon && !icon_right) ? <Icon name={icon}/> : null}
        {text ? <span>{text}</span> : null}
        {(icon && icon_right) ? <Icon name={icon}/> : null}
      </button>
      {loading ? <div className="button_spinner"><div><svg className="spinner" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"><circle className="path" fill="none" strokeWidth="11" strokeLinecap="butt" cx="50" cy="50" r="40"></circle></svg></div></div> : null}
    </div>
  </>
}

export function ComboButton({children}) {
  return (
    <div className="combo_button buttons">
      {children}
    </div>
  )
}

export function RoundIconButton({ icon, className, onClick, onContextMenu, ...args }) {
  let { disabled, readOnly, obviated } = useFormControl({ ...args });

  return (
    <button
      className={"round_icon_button" + (className ? " "+className : "") + ((disabled) ? " disabled" : "")}
      onClick={(onClick && !(disabled || obviated)) ? onClick : (() => {})}
      onContextMenu={(onContextMenu && !(disabled || obviated)) ? onContextMenu : undefined}
      inert={(disabled || obviated) ? "true" : undefined}
    >
      <Icon name={icon}/>
    </button>
  )
}

// Standard args: name, value, default_value, onChange, error, required

export const Textbox = forwardRef(function Textbox({ title, multiline, height, markdown = false, markdown_minimal = false, placeholder, hint, onEnter, onKeyDown, autofocus, type, min, max, autocomplete, children, validate, fullWidth, className = "", ...args }, ref) {

  const { name, value, setValue, error, setError, required, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {
      if (required && (value || "").trim() == "") {
        return required;
      }
      if (required && validate == "email") {
        if (!validateEmail(value)) {
          return "Please enter a valid email address";
        }
      }
      if (required && type == "number") {
        if (isNaN(value)) {// || value.includes(".")
          return "Please enter a number";
        } else if (min != null && Number(value) < min) {
          return "Number can't be under " + min;
        } else if (max != null && Number(value) > max) {
          return "Number can't be over " + max;
        }
      }
    },
    formatValue: (value, pretty) => type == "number" ? ((String(value || "").trim().length > 0 && !isNaN(value)) ? Number(value) : null) : ((pretty ? (value || "").trim() : (value || "")) || null),
    parseValue: (value) => type == "number" ? (value != null ? String(value) : "") : (value || ""),
    getFocusElement: ({name, parent}) => markdown ? parent.querySelector(`.markdown_editor_${name}`) : parent.querySelector(`.textbox[name="${name}"]`),
    ...args
  });

  const internal_ref = useRef();
  const isUserTyping = useRef(false);

  useImperativeHandle(ref, () => internal_ref.current);

  useEffect(() => {
    if (autofocus) {
      if (autofocus == "always" || (internal_ref.current && (markdown ? internal_ref.current.getMarkdown() == "" : internal_ref.current.value == ""))) {
        window.requestAnimationFrame(() => {
          internal_ref.current.focus();
        });
      }
    }
  }, [autofocus])

  useEffect(() => {
    if (!markdown && internal_ref.current && !isUserTyping.current) {
      if (internal_ref.current.value !== value) {
        internal_ref.current.value = value;
      }
    }
  }, [value, markdown]);

  const markdownDefaultValueLogic = useRef({
    initial_value: "",
    initial_markdown: ""
  }); //Store the initial value and markdown text (normalized). If the markdown text is the same as the initial markdown text, give the initial value (non-normalized) instead, so that the Save Changes button doesn't light up when the user hasn't changed anything

  useEffect(() => {
    if (markdown && internal_ref.current && value !== (internal_ref.current.getMarkdown() == markdownDefaultValueLogic.current?.initial_markdown ? markdownDefaultValueLogic.current?.initial_value : internal_ref.current.getMarkdown())) {
      internal_ref.current.setMarkdown(value);//.replaceAll("<", "\\<")
      window.requestAnimationFrame(() => {
        markdownDefaultValueLogic.current = {
          initial_value: value,
          initial_markdown: internal_ref.current.getMarkdown()
        }
      })
    }
  }, [value, markdown]);

  const onChangeTimeout = useRef(null);

  let textbox;
  if (markdown) {
    textbox = <MDXEditor 
      autoFocus={autofocus == "always" ? { defaultSelection: "end" } : undefined}
      ref={internal_ref} 
      markdown={""} 
      readOnly={readOnly || obviated}
      onChange={(text) => {
        let pending_value = markdownDefaultValueLogic.current.initial_markdown == text ? markdownDefaultValueLogic.current.initial_value : text;
        if (onChangeTimeout.current) {
          clearTimeout(onChangeTimeout.current);
        }
        onChangeTimeout.current = setTimeout(() => {
          setValue(pending_value);
          setError(null);
          onChangeTimeout.current = null;
        }, value == "" ? 0 : 300);
      }} 
      plugins={[
        headingsPlugin({
          allowedHeadingLevels: [1, 2, 3, 4]
        }),
        quotePlugin(),
        listsPlugin(),
        toolbarPlugin({
          toolbarClassName: 'markdown_toolbar',
          toolbarContents: () => (
            <>
              {/* {' '} */}
              <BoldItalicUnderlineToggles />
              <ListsToggle options={["bullet"]} />
              {!markdown_minimal ? <BlockTypeSelect /> : null}
              {/* <UndoRedo /> */}
            </>
          )
        }),
        markdownShortcutPlugin(),
        thematicBreakPlugin()
      ]}
      contentEditableClassName={`markdown_editor markdown ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${readOnly ? "readOnly" : ""} markdown_editor_${name} ${className}`}
    />
  } else if (multiline) {
    textbox = <TextareaAutosize
      ref={internal_ref}
      name={name}
      id={name}
      className={`textbox ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${readOnly ? "readOnly" : ""} ${className}`}
      readOnly={disabled || readOnly || obviated}
      inert={(disabled || obviated) ? "true" : undefined}
      type="text"
      placeholder={placeholder}
      onChange={useCallback((event) => {
        isUserTyping.current = true;
        setValue(event.target.value);
        setError(null);
        Promise.resolve().then(() => {
          isUserTyping.current = false;
        });
      }, [setValue, setError])}
      onKeyDown={useCallback((event) => {
        if (!(disabled || readOnly || obviated)) {
          if (onKeyDown) {
            onKeyDown(event);
          }
          if (event.key === 'Enter' && !event.shiftKey) {
            if (onEnter) {
              onEnter(event);
              event.preventDefault();
            }
          }
        }
      }, [disabled, readOnly, obviated, onKeyDown, onEnter])}
      defaultValue={value}
      minRows={(height == "long" ? 4 : (height == "short" ? 1 : (height || 2)))}
      maxRows={Math.max(8, !isNaN(height) ? height : 0)}
    />
  } else {
    textbox = <input
      ref={internal_ref}
      name={name}
      id={name}
      className={`textbox ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${readOnly ? "readOnly" : ""} ${className}`}
      readOnly={disabled || readOnly || obviated}
      inert={(disabled || obviated) ? "true" : undefined}
      type={type || "text"}
      autoComplete={autocomplete ? (autocomplete === true ? "on" : autocomplete) : "nope"}
      placeholder={placeholder}
      onChange={(event) => {
        isUserTyping.current = true;
        setValue(event.target.value);
        setError(null);
        Promise.resolve().then(() => {
          isUserTyping.current = false;
        });
      }}
      onKeyDown={(event) => {
        if (!(disabled || readOnly || obviated)) {
          if (onKeyDown) {
            onKeyDown(event);
          }
          if (event.key === 'Enter') {
            if (onEnter) {
              onEnter(event);
            }
          }
        }
      }}
      defaultValue={value}
    />
  }

  return (
    <div style={{minWidth: "0px"}} className={(markdown ? "markdown_textbox_container" : "") + (fullWidth ? " full_width" : "")}>
      {title ? <label htmlFor={name}>{title}</label> : null}
      {children ? <div className="textbox_units_container"><div>{textbox}</div><div>{children}</div></div> : textbox}
      {error ? <div className="input_error">{error}</div> : (hint ? <div className="input_hint">{hint}</div> : null)}
    </div>
  )
})

export function TextboxGroup({textboxes, multiline, autofocus, ...args}) {
  const { name, value, updateValue, error, updateError } = useFormField({
    validateValue: useCallback(({value, required}) => {
      if (required) {
        let validated_boxes = textboxes.map((textbox, index) => (value?.[index] || "").trim().length > 0);
        if (validated_boxes.some(a => a == false)) {
          return validated_boxes.map(a => a ? null : required);
        }
      }
    }, [textboxes]),
    formatValue: useCallback((value, pretty) => textboxes.map((textbox, index) => (pretty ? (value?.[index] || "").trim() : (value?.[index] || "")) || null), []),
    parseValue: useCallback((value) => textboxes.map((textbox, index) => value?.[index] || ""), []),
    getFocusElement: useCallback(({name, parent, error}) => parent.querySelectorAll(`[name="${name}"] .textbox`)[error?.findIndex(a => a != null) || 0], []),
    ...args
  });

  const handleOnChange = useCallback((index, new_value) => {
    updateValue(draft => {
      draft[index] = new_value;
      return draft;
    });
    
    updateError(draft => {
      if (draft) {
        draft[index] = null;
      }
      return draft;
    });
  }, [updateValue, updateError]);

  return (
    textboxes.map((textbox, index) => (
      <div key={index} name={name}>
        <MemoizedTextboxGroupItem textbox={textbox} value={value[index]} handleOnChange={handleOnChange} index={index} error={error?.[index]} multiline={multiline} autofocus={autofocus && index == 0}/>
        {index < textboxes.length-1 ? <Spacing/> : null}
      </div>
    ))
  )
}

// This reduces re-renders barely
const MemoizedTextboxGroupItem = memo(function MemoizedTextboxGroupItem({ textbox, index, handleOnChange, value, error, multiline, autofocus }) {
  return (
    <Textbox title={textbox.title} value={value} onChange={(new_value) => handleOnChange(index, new_value)} error={error} multiline={multiline} autofocus={autofocus && index == 0} hint={textbox.hint} placeholder={textbox.placeholder}/>
  )
})

export function Spacing() {
  return <div style={{ paddingTop: 16 }} />
}

export function ThreeQuarterSpacing() {
  return <div style={{ paddingTop: 12 }} />
}

export function HalfSpacing() {
  return <div style={{ paddingTop: 8 }} />
}

const RadioContext = createContext(null);

export function RadioGroup({title, subtitle, hint, children, ...args}) {

  const { name, value, setValue, error, setError, required } = useFormField({
    validateValue: ({value, required}) => {
      if (required && !value) {
        return required;
      }
    },
    formatValue: (value, pretty) => value || null,
    parseValue: (value) => value || null,
    getFocusElement: ({name, parent}) => parent.querySelector(`.radio_group[name="${name}"]`),
    ...args
  });

  function handleChange(newvalue) {
    setValue(newvalue);
    setError(null);
  }
  
  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      {subtitle ? <div className="input_subtitle">{subtitle}</div> : null}
      <div className="radio_group" role="radiogroup" name={name} id={name}>
        <RadioContext.Provider value={{value, onChange: handleChange}}>
          {children}
        </RadioContext.Provider>
        {error ? <div className="input_error">{error}</div> : (hint ? <div className="input_hint">{hint}</div> : null)}
      </div>
    </>
  )
}

export function RadioOption({name, text, title}) {
  const context = useContext(RadioContext);

  function handleClick() {
    context.onChange(name);
  }

  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      <div className={"checkbox_option" + (context.value == name ? " checked" : "")} tabIndex="0" name={name} onClick={handleClick} role="radio" aria-label={text} aria-checked={context.value == name ? "true" : "false"}>
        <div className="checkbox">
          <Icon name={context.value == name ? "radiobox-marked" : "radiobox-blank"}/>
        </div>
        <div className="label">{text}</div>
      </div>
    </>
  )
}

export function Checkbox({title, text, ariaLabel, ...args}) {

  const { name, value, setValue, error, setError, required, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {},
    formatValue: (value, pretty) => value || false,
    parseValue: (value) => value || false,
    getFocusElement: ({name, parent}) => parent.querySelector(`.checkbox_option[name="${name}"]`),
    ...args
  });

  function handleChange(newvalue) {
    setValue(newvalue);
    setError(null);
  }

  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      <div 
        className={"checkbox_option" + (value ? " checked" : "")} 
        tabIndex="0" 
        name={name} 
        onClick={() => handleChange(!value)} 
        role="checkbox" 
        aria-label={ariaLabel || text} 
        aria-checked={value ? "true" : "false"}
        inert={(disabled || obviated) ? "true" : undefined}
      >
        <div className="checkbox">
          <Icon name={value ? "checkbox-marked" : "checkbox-blank-outline"}/>
        </div>
        <div className="label">{text}</div>
      </div>
      {error ? <div className="input_error">{error}</div> : null}
    </>
  )
}

const CheckboxGroupContext = createContext(null);

export function CheckboxGroup({title, children, ...args}) {

  const { name, value, setValue, error, setError, required } = useFormField({
    validateValue: ({value, required}) => {
      if (required && value.length == 0) {
        return required;
      }
    },
    formatValue: (value, pretty) => value || [],
    parseValue: (value) => value || [],
    getFocusElement: ({name, parent}) => parent.querySelector(`.checkbox_group[name="${name}"]`),
    ...args
  });

  function handleChange(name) {
    if (value.includes(name)) {
      setValue(value.filter(item => item != name));
    } else {
      setValue([...value, name]);
    }
    setError(null);
  }
  
  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      <div className="checkbox_group" role="group" name={name} id={name}>
        <CheckboxGroupContext.Provider value={{value, onChange: handleChange}}>
          {children}
        </CheckboxGroupContext.Provider>
        {error ? <div className="input_error">{error}</div> : null}
      </div>
    </>
  )
}

export function CheckboxOption({name, text}) {
  const context = useContext(CheckboxGroupContext);

  const { disabled, readOnly, obviated } = useFormControl({});

  function handleClick() {
    context.onChange(name);
  }

  return (
    <div 
      className={"checkbox_option" + (context.value.includes(name) ? " checked" : "")} 
      tabIndex="0" 
      name={name} 
      onClick={handleClick} 
      role="checkbox" 
      aria-label={text} 
      aria-checked={context.value.includes(name) ? "true" : "false"}
      inert={(disabled || obviated) ? "true" : undefined}
    >
      <div className="checkbox">
        <Icon name={context.value.includes(name) ? "checkbox-marked" : "checkbox-blank-outline"}/>
      </div>
      <div className="label">{text}</div>
    </div>
  )
}

// const ChipsContext = createContext(null);

// export function ChipsGroup({title, name, children, data, updateData, error, updateError}) {
//   let effective_value = data[name] || { items: [], other: "" };

//   function handleChipChange(chiptext, checked) {
//     updateData(draft => {
//       if (checked) {
//         draft[name].items.push(chiptext);
//       } else {
//         draft[name].items = draft[name].items.filter(item => item != chiptext);
//       }
//     })
//     updateError(draft => {draft[name] = null});
//   }

//   function handleOtherChange(newvalue) {
//     updateData(draft => {draft[name].other = newvalue})
//     updateError(draft => {draft[name] = null});
//   }

//   useEffect(function() {
//     if (!data[name]) {
//       updateData(draft => {draft[name] = { items: [], other: "" }})
//     }
//   }, [data, name])
  
//   return (
//     <>
//       <label>{title}</label>
//       <div className="chips_group">
//         <ChipsContext.Provider value={{value: effective_value.items, onChipChange: handleChipChange}}>
//           {children}
//         </ChipsContext.Provider>
//         {error[name] ? <div className="input_error">{error[name]}</div> : null}
//       </div>
//       <Textbox placeholder="Other..." value={effective_value.other} onChange={handleOtherChange} />
//     </>
//   )
// }

// export function ChipCheckbox({text}) {
//   const context = useContext(ChipsContext);

//   let checked = context.value.includes(text);

//   function handleClick() {
//     context.onChipChange(text, !checked);
//   }

//   return (
//     <div className={"chip_checkbox" + (checked ? " checked" : "")} tabIndex="0" onClick={handleClick} role="checkbox" aria-label={text} aria-checked={checked ? "true" : "false"}>
//       <div>
//         <Icon name={checked ? "checkbox-marked" : "checkbox-blank-outline"}/>
//       </div>
//       <div>{text}</div>
//     </div>
//   )
// }

export function Dropdown({ title, other = false, children, ...args }) {

  const { name, value, setValue, error, setError, required, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {
      if (required && !value) {
        return required;
      }
    },
    formatValue: (value, pretty) => value || null,
    parseValue: (value) => value != null ? value : null,
    getFocusElement: ({name, parent}) => parent.querySelector(`.dropdown[name="${name}"]`),
    ...args
  });

  function handleChange(newvalue) {
    if (other) {
      setValue(newvalue == "other" ? "" : newvalue);
    } else {
      setValue(newvalue);
    }
    setError(null);
  }

  function handleChangeOther(newvalue) {
    setValue(newvalue);
    setError(null);
  }

  const items = [];
  for (const child of children) {
    if (child.type === DropdownHeading) {
      items.push({
        element: <div className="dropdown_heading">{child.props.text}</div>,
        height: 31
      });
    } else {
      if (!other && !child.props.name) {
        throw new Error("DropdownOption must have a name prop unless other={true}");
      }
      items.push({
        name: other ? child.props.text : child.props.name,
        element: child.props.text,
        height: 42
      });
    }
  }

  if (other) {
    items.push({
      name: "other",
      element: "Other...",
      height: 42
    })
  }
  
  return (
    <>
      {title ? <label htmlFor={name}>{title}</label> : null}
      <GenericDropdown name={name} items={items} menuPaddingHeight={10} value={other ? ((items.some(a => a.name == value) || value == null) ? value : "other") : value} onChange={handleChange} disabled={disabled || readOnly || obviated} className={`dropdown ${disabled ? "disabled" : (readOnly ? "readOnly" : "")} ${error ? " error" : ""}`}>
        {children}
      </GenericDropdown>
      {
        (other && !(items.some(a => a.name == value) || value == null)) ? (
          <>
            <HalfSpacing/>
            <Textbox value={value} onChange={handleChangeOther} placeholder="Other..." autofocus={true} disabled={disabled || readOnly || obviated} />
          </>
        ) : null
      }
      {error ? <div className="input_error">{error}</div> : null}
    </>
  )
}

// name is optional if other = true for dropdown
export function DropdownOption({ name, text }) {
  return null;
}

export function DropdownHeading({ text }) {
  return null;
}

export function FileLocation({ title, type, ...args }) {

  const { name, value: folder, setValue, error, setError, hint, disabled, readOnly, obviated } = useFormField({
    validateValue: ({value, required}) => {},
    formatValue: (value, pretty) => value,
    parseValue: (value) => value,
    getFocusElement: ({name, parent}) => parent.querySelector(`.file_location_box[name="${name}"]`),
    ...args
  });

  const location_icon = folder ? (folder._is_shared ? "folder-account" : "folder") : (type === "scenario" ? "forum-outline" : "bullseye-arrow");
  const location_name = folder ? folder.title : (type === "scenario" ? "My scenarios" : "My rubrics");

  const [openFilePickerModal] = useFilePickerModal();

  const handleClick = () => {
    openFilePickerModal({
      mode: "save",
      type: type,
      initial_folder_id: folder?._id || null,
      callback: ({ folder, closeModal }) => {
        setValue(folder);
        closeModal();
      }
    })
  }

  return (
    <div style={{minWidth: "0px"}}>
      {title ? <label htmlFor={name}>{title}</label> : null}
      <div className={`file_location_box ${error ? "error" : ""} ${disabled ? "disabled" : ""} ${readOnly ? "readOnly" : ""}`} name={name} inert={(disabled || obviated) ? "true" : undefined} onClick={handleClick} tabIndex="0">
        <div>
          <div>
            <Icon name={location_icon}/>
          </div>
          <div>{location_name}</div>
        </div>
        <div>
          <Icon name="folder-outline"/>
        </div>
      </div>
      {error ? <div className="input_error">{error}</div> : (hint ? <div className="input_hint">{hint}</div> : null)}
    </div>
  )
}